// @ts-check
// Canvas do bot (contrato de bots, seção 22.2): o canvas horizontal dos fluxos com as regras da árvore do bot (um ramo
// por botão ou opção, mais "outra resposta" e "sem resposta"; "Ir para" e "Encerrar" sem "+" depois, com o atalho para
// o destino), o cartão "Quando o bot começa", o círculo e o resumo de cada bloco e, com "Números", quem está em cada
// bloco agora e os números do período (chegaram, inválidas, sem resposta) em cada bloco e em cada ramo.
import { html } from 'htm/preact';
import { useMemo } from 'preact/hooks';
import { formatNumber } from '../../lib/format.js';
import { moveBotFocus } from '../../lib/bot-canvas-keyboard.js';
import { botBranchKeysOf, isBotSplitNode, isBotTerminalNode } from '../../lib/bot-tree.js';
import { FlowCanvas } from '../flows/flow-canvas.js';
import { BotEntryCard } from './bot-entry.js';
import { BotCategoryCircle, botBranchLabel, botBranchTone, botProblemText, describeBotNode } from './bot-ui.js';
import { t } from '../../i18n/index.js';

/**
 * @typedef {import('../../../../shared/bots/bot-definition.js').BotDefinition} BotDefinition
 * @typedef {import('../../../../shared/bots/bot-definition.js').BotNode} BotNode
 * @typedef {import('../../../../shared/bots/bot-summary.js').BotSummaryLookups} BotSummaryLookups
 * @typedef {import('../../lib/bot-validation.js').BotEditorProblem} BotEditorProblem
 * @typedef {import('../../lib/bot-editor-state.js').BotInsertPoint} BotInsertPoint
 * @typedef {import('../../data/bots.js').BotNodeCounts} BotNodeCounts
 * @typedef {import('../../lib/bot-stats.js').BotStats} BotStats
 * @typedef {import('./bot-lookups.js').BotLookupData} BotLookupData
 */

/**
 * Pílulas embaixo do bloco: "agora" (ao vivo) e, com o período, chegaram, inválidas e sem resposta.
 * @param {{ now: number | null, stats: { arrived: number, invalid: number, timeouts: number, failed: number } | null }} props
 */
function BotNodeCounts({ now, stats }) {
  /** @type {Array<{ key: string, icon: string, value: number, label: string, tone?: string }>} */
  const pills = [];
  if (now !== null) pills.push({ key: 'now', icon: 'IconHourglass', value: now, label: t('bots.counts.now', { count: now }) });
  if (stats) {
    pills.push({ key: 'arrived', icon: 'IconArrowRight', value: stats.arrived, label: t('bots.counts.arrived', { count: stats.arrived }) });
    if (stats.invalid > 0) pills.push({ key: 'invalid', icon: 'IconAlertTriangle', value: stats.invalid, label: t('bots.counts.invalid', { count: stats.invalid }), tone: 'warning' });
    if (stats.timeouts > 0) pills.push({ key: 'timeouts', icon: 'IconClock', value: stats.timeouts, label: t('bots.counts.timeouts', { count: stats.timeouts }) });
    if (stats.failed > 0) pills.push({ key: 'failed', icon: 'IconAlertCircle', value: stats.failed, label: t('bots.counts.failed', { count: stats.failed }), tone: 'danger' });
  }
  if (pills.length === 0) return null;
  return html`<div class="flow-node-counts">
    ${pills.map((pill) => html`<span key=${pill.key} class="flow-count-pill" data-tone=${pill.tone} data-empty=${pill.value === 0 ? 'true' : undefined}
      title=${pill.label} aria-label=${pill.label}>${formatNumber(pill.value)}</span>`)}
  </div>`;
}

/**
 * @param {{ definition: BotDefinition, lookups: BotSummaryLookups, lookupData: BotLookupData, problems: BotEditorProblem[], problemsByNode: Map<string, BotEditorProblem[]>,
 *   stepNumbers: Map<string, number>, selectedNodeId: string | null, focusTarget: string, onFocusTarget: (target: string) => void, zoom: number,
 *   onZoom: (zoom: number) => void, showCounts: boolean, canShowCounts: boolean, onToggleCounts: () => void, counts: BotNodeCounts | null,
 *   stats?: BotStats | null, isReadOnly?: boolean, activePointKey?: string | null, isDragging?: boolean, isPulsing?: boolean,
 *   insertDisabledReason?: (point: BotInsertPoint) => string | null, onInsert?: (point: BotInsertPoint) => void, onDropAction?: (point: BotInsertPoint, itemKey: string) => void,
 *   onSelectNode?: (nodeId: string) => void, onDeleteNode?: (nodeId: string) => void, onOpenActions?: () => void, onActivateTarget?: (target: string) => void,
 *   onInsertAfterTarget?: (target: string) => void, onDeleteTarget?: (target: string) => void, onEscape?: () => void, onEditEntry?: () => void,
 *   isEntrySelected?: boolean, ariaLabel: string, announcement?: string, centerSignal?: number, className?: string, startedCount?: number | null }} props
 */
export function BotCanvas(props) {
  const {
    definition, lookups, lookupData, problems, problemsByNode, stepNumbers, selectedNodeId, focusTarget, counts, stats = null, showCounts, isReadOnly = false,
    onSelectNode, onEditEntry,
  } = props;
  const describe = useMemo(() => {
    /** @type {Map<string, ReturnType<typeof describeBotNode>>} */
    const cache = new Map();
    return (/** @type {BotNode} */ node) => {
      const key = `${node.id}:${JSON.stringify(node.config)}:${node.name ?? ''}`;
      if (!cache.has(key)) cache.set(key, describeBotNode(node, lookups));
      return /** @type {ReturnType<typeof describeBotNode>} */ (cache.get(key));
    };
  }, [lookups, definition]);
  const tree = {
    lookups: /** @type {any} */ ({}), problemsByNode, stepNumbers, selectedNodeId, focusTarget, counts: /** @type {any} */ (null), showCounts, flowId: null, isReadOnly,
    activePointKey: props.activePointKey ?? null, isDragging: props.isDragging ?? false, isPulsing: props.isPulsing ?? false, period: {},
    insertDisabledReason: /** @type {any} */ (props.insertDisabledReason ?? (() => null)),
    onInsert: /** @type {any} */ (props.onInsert ? (/** @type {BotInsertPoint} */ point) => props.onInsert?.(point) : undefined),
    onDropAction: /** @type {any} */ (props.onDropAction),
    onSelectNode, onDeleteNode: props.onDeleteNode,
    branchKeysOf: botBranchKeysOf, isSplitNode: isBotSplitNode, isTerminalNode: isBotTerminalNode, describe,
    branchLabel: (/** @type {string} */ branchKey, /** @type {string | null} */ parentId) => {
      const parent = parentId ? findNode(definition, parentId) : null;
      return botBranchLabel(parent, branchKey);
    },
    renderBranchLabel: (/** @type {BotNode} */ node, /** @type {string} */ branchKey) => {
      const count = showCounts && stats ? Number(stats.nodes?.[node.id]?.branches?.[branchKey] ?? 0) : null;
      const label = botBranchLabel(node, branchKey);
      return html`<span class="flow-branch-label-wrap">
        <span class="flow-branch-label bot-branch-label" data-tone=${botBranchTone(branchKey)} title=${label}>${label}</span>
        ${count !== null ? html`<span class="flow-branch-count" data-empty=${count === 0 ? 'true' : undefined} aria-label=${t('bots.counts.branch', { count, branch: label })}>
          ${formatNumber(count)}</span>` : null}
      </span>`;
    },
    renderCircle: (/** @type {BotNode} */ node) => html`<${BotCategoryCircle} type=${node.type} />`,
    renderNodeCounts: (/** @type {BotNode} */ node) => {
      if (!showCounts) return null;
      const now = counts ? Number(counts.nodes?.[node.id]?.now ?? 0) : null;
      const nodeStats = stats?.nodes?.[node.id] ?? null;
      return html`<${BotNodeCounts} now=${now} stats=${nodeStats} />`;
    },
    problemText: botProblemText,
    nodeShortcut: (/** @type {BotNode} */ node) => {
      if (node.type !== 'GOTO') return null;
      const targetId = typeof node.config?.targetNodeId === 'string' ? node.config.targetNodeId : null;
      const name = targetId ? lookups.nodeTitle?.(targetId) ?? null : null;
      return { label: name ? t('bots.canvas.gotoShortcut', { name }) : t('bots.canvas.gotoMissing'), targetId: name ? targetId : null };
    },
    onGoToNode: (/** @type {string} */ nodeId) => {
      props.onFocusTarget(nodeId);
      if (!isReadOnly) onSelectNode?.(nodeId);
      requestAnimationFrame(() => document.querySelector(`[data-focus-id="${CSS.escape(nodeId)}"]`)?.scrollIntoView({ block: 'center', inline: 'center', behavior: 'smooth' }));
    },
    emptyRootLabel: t('bots.canvas.emptyBot'),
  };
  return html`<${FlowCanvas} definition=${/** @type {any} */ (definition)} tree=${tree} entryProblems=${[]} entryCounts=${null} focusTarget=${focusTarget}
    onFocusTarget=${props.onFocusTarget} zoom=${props.zoom} onZoom=${props.onZoom} showCounts=${showCounts} canShowCounts=${props.canShowCounts}
    onToggleCounts=${props.onToggleCounts} onOpenActions=${props.onOpenActions} onActivateTarget=${props.onActivateTarget} onInsertAfterTarget=${props.onInsertAfterTarget}
    onDeleteTarget=${props.onDeleteTarget} onEscape=${props.onEscape} ariaLabel=${props.ariaLabel} announcement=${props.announcement ?? ''} isReadOnly=${isReadOnly}
    centerSignal=${props.centerSignal ?? 0} className=${`bot-canvas ${props.className ?? ''}`} moveFocus=${moveBotFocus} actionsLabel=${t('bots.canvas.blocks')}
    renderEntry=${(/** @type {{ isFocused: boolean }} */ args) => html`<${BotEntryCard} definition=${definition} lookupData=${lookupData}
      problems=${problems.filter((problem) => problem.area === 'TRIGGERS')} isFocused=${args.isFocused} isSelected=${props.isEntrySelected ?? false}
      isReadOnly=${isReadOnly} onEdit=${onEditEntry} startedCount=${showCounts && stats ? stats.totals.started : null} />`} />`;
}

/** @param {BotDefinition} definition @param {string} nodeId @returns {BotNode | null} */
function findNode(definition, nodeId) {
  /** @type {BotNode | null} */
  let found = null;
  /** @param {BotNode[]} list */
  const visit = (list) => {
    for (const node of list ?? []) {
      if (found) return;
      if (node.id === nodeId) {
        found = node;
        return;
      }
      for (const key of botBranchKeysOf(node)) visit(node.branches?.[key] ?? []);
    }
  };
  visit(definition.nodes);
  return found;
}
